/**
 * Dockal Registry Theme Colors - Unified Design System
 *
 * Now using Ant Design's official Cyan palette for primary colors
 * Cyan represents: Hope, Strong
 *
 * Reference: https://ant.design/docs/spec/colors
 * Package: @ant-design/colors
 *
 * Edit these values to change the theme across the entire application.
 * After changing colors, restart the dev server to see the changes.
 */

// Ant Design Cyan Palette (10 shades)
// ['#E6FFFB', '#B5F5EC', '#87E8DE', '#5CDBD3', '#36CFC9', '#13C2C2', '#08979C', '#006D75', '#00474F', '#002329']

interface Theme {
  // PRIMARY BRAND COLORS (Ant Design Cyan)
  primary: string;
  primaryHover: string;
  primaryActive: string;
  primaryLight: string;
  primaryLighter: string;

  // SECONDARY COLORS (For variety and hierarchy)
  secondary: string;
  secondaryHover: string;
  secondaryLight: string;

  // ACCENT COLORS
  accent: string;
  accentDark: string;
  accentLight: string;

  // GRADIENT COLORS
  gradientStart: string;
  gradientEnd: string;
  gradientMid: string;
  gradientPrimaryStart: string;
  gradientPrimaryEnd: string;

  // TEXT COLORS
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textLight: string;
  textWhite: string;
  textOffWhite: string;
  textWhiteSecondary: string;
  textWhiteTertiary: string;

  // BACKGROUND COLORS
  bgWhite: string;
  bgLight: string;
  bgLighter: string;
  bgGray: string;
  bgDark: string;
  bgBlack: string;
  bgOffWhite: string;

  // BORDER COLORS
  border: string;
  borderLight: string;
  borderDark: string;
  borderFocus: string;
  borderError: string;

  // SEMANTIC COLORS (Status indicators)
  success: string;
  successDark: string;
  successLight: string;
  successBorder: string;

  error: string;
  errorDark: string;
  errorLight: string;
  errorBorder: string;

  warning: string;
  warningDark: string;
  warningLight: string;
  warningBorder: string;

  info: string;
  infoDark: string;
  infoLight: string;
  infoBorder: string;

  // SPECIAL USE CASES
  shadowLight: string;
  shadow: string;
  shadowMedium: string;
  shadowDark: string;
  shadowXl: string;

  focusRing: string;
  focusRingMedium: string;
  focusRingSolid: string;

  glassWhite: string;
  glassDark: string;
  glassLight: string;
  glassBorder: string;
  glassBorderDark: string;

  certificateBg: string;
  documentBg: string;

  hoverBg: string;
  hoverBgDark: string;

  selection: string;
  selectionBorder: string;

  disabled: string;
  disabledBg: string;

  // TYPOGRAPHY
  fontFamily: string;
}

const theme: Theme = {
  // ============================================
  // PRIMARY BRAND COLORS (Ant Design Cyan)
  // ============================================
  primary: '#08979C',           // Cyan-7: Main brand color (Darker, more professional)
  primaryHover: '#006D75',      // Cyan-8: Hover state
  primaryActive: '#00474F',     // Cyan-9: Active/pressed state
  primaryLight: '#B5F5EC',      // Cyan-2: Light backgrounds
  primaryLighter: '#E6FFFB',    // Cyan-1: Very light backgrounds

  // ============================================
  // SECONDARY COLORS (For variety and hierarchy)
  // ============================================
  secondary: '#0e7490',         // Secondary brand color (alternative actions)
  secondaryHover: '#0c6380',    // Secondary hover
  secondaryLight: '#e0f2f7',    // Secondary light

  // ============================================
  // ACCENT COLORS
  // ============================================
  accent: '#22d3ee',            // Accent highlights (badges, tags)
  accentDark: '#06b6d4',        // Darker accent
  accentLight: '#67e8f9',       // Lighter accent

  // ============================================
  // GRADIENT COLORS
  // ============================================
  gradientStart: '#667eea',     // Gradient start (purple)
  gradientEnd: '#764ba2',       // Gradient end (purple-pink)
  gradientMid: '#08979C',       // Gradient middle (cyan brand - creates purple→cyan→purple blend)
  gradientPrimaryStart: '#08979C', // Primary gradient start (Cyan-7)
  gradientPrimaryEnd: '#006D75',   // Primary gradient end (Cyan-8)

  // ============================================
  // TEXT COLORS
  // ============================================
  textPrimary: '#193156',       // Primary text (headings, important text)
  textSecondary: '#68717a',     // Secondary text (descriptions)
  textTertiary: '#8c8c8c',      // Tertiary text (hints, placeholders)
  textLight: '#999',            // Light text (disabled, subtle)
  textWhite: '#FFFFFF',         // White text (on dark backgrounds)
  textOffWhite: 'rgba(255, 255, 255, 0.85)', // Off-white text
  textWhiteSecondary: 'rgba(255, 255, 255, 0.75)', // White secondary
  textWhiteTertiary: 'rgba(255, 255, 255, 0.65)', // White tertiary

  // ============================================
  // BACKGROUND COLORS
  // ============================================
  bgWhite: '#FFFFFF',           // Pure white background
  bgLight: '#f5f5f5',           // Light gray background
  bgLighter: '#f5f7fa',         // Lighter gray background
  bgGray: '#fafafa',            // Gray background (cards)
  bgDark: '#343A40',            // Dark background (sidebar)
  bgBlack: '#141414',           // Black background (dark mode)
  bgOffWhite: '#E1D9D1',        // Off-white background

  // ============================================
  // BORDER COLORS
  // ============================================
  border: '#ebedf0',            // Default border (1px)
  borderLight: '#f0f0f0',       // Light border
  borderDark: '#d9d9d9',        // Darker border
  borderFocus: '#08979C',       // Focus state border (Cyan-7)
  borderError: '#ff4d4f',       // Error state border

  // ============================================
  // SEMANTIC COLORS (Status indicators)
  // ============================================
  // Success
  success: '#52c41a',           // Success green
  successDark: '#3f8600',       // Dark success
  successLight: '#f6ffed',      // Light success background
  successBorder: '#b7eb8f',     // Success border

  // Error
  error: '#ff4d4f',             // Error red
  errorDark: '#cf1322',         // Dark error
  errorLight: '#fff2f0',        // Light error background
  errorBorder: '#ffccc7',       // Error border

  // Warning
  warning: '#faad14',           // Warning orange
  warningDark: '#d48806',       // Dark warning
  warningLight: '#fffbe6',      // Light warning background
  warningBorder: '#ffe58f',     // Warning border

  // Info
  info: '#1890ff',              // Info blue
  infoDark: '#096dd9',          // Dark info
  infoLight: '#e6f7ff',         // Light info background
  infoBorder: '#91d5ff',        // Info border

  // ============================================
  // SPECIAL USE CASES
  // ============================================
  // Shadows
  shadowLight: 'rgba(0, 0, 0, 0.04)',
  shadow: 'rgba(0, 0, 0, 0.1)',
  shadowMedium: 'rgba(0, 0, 0, 0.15)',
  shadowDark: 'rgba(0, 0, 0, 0.2)',
  shadowXl: 'rgba(0, 0, 0, 0.25)',

  // Focus/Glow effects
  focusRing: 'rgba(25, 158, 169, 0.1)',
  focusRingMedium: 'rgba(25, 158, 169, 0.2)',
  focusRingSolid: 'rgba(25, 158, 169, 0.3)',

  // Glassmorphism
  glassWhite: 'rgba(255, 255, 255, 0.95)',
  glassDark: 'rgba(0, 0, 0, 0.6)',
  glassLight: 'rgba(255, 255, 255, 0.2)',
  glassBorder: 'rgba(255, 255, 255, 0.18)',
  glassBorderDark: 'rgba(255, 255, 255, 0.3)',

  // Certificate & Document specific
  certificateBg: '#EBF9F5',     // Certificate background
  documentBg: '#fafafa',        // Document viewer background

  // Hover states (for interactive elements)
  hoverBg: '#f0f5ff',          // Light blue hover background
  hoverBgDark: '#e6f4ff',      // Darker hover background

  // Selection
  selection: '#e6f4ff',         // Selected item background
  selectionBorder: '#0e7490',   // Selected item border

  // Disabled
  disabled: '#d9d9d9',          // Disabled border/text
  disabledBg: '#f5f5f5',        // Disabled background

  // ============================================
  // TYPOGRAPHY (Single Source of Truth)
  // To change font: Edit fontFamily here, restart dev server
  // ============================================
  fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",  // Primary font stack
};

export default theme;
